import dayjs from 'dayjs';
import { groupBy } from 'lodash-es';
import { useMemo } from 'react';

import { VStack, cn } from '@/package';
import { useNotificationStore } from '@/store';
import type { DisplayedNotification } from '@/type';

import NotificationItem from './NotificationItem';
import type { NotificationGroupProps } from './type';
import { makeNotification } from './util';

const isToday = (strDate: string) => {
  const diffHour = dayjs().diff(strDate) / 3600000;

  return diffHour <= 24;
};

const NotificationList = () => {
  const { notificationRawList, getParsedNotificationList } =
    useNotificationStore();
  const list = useMemo(
    () =>
      getParsedNotificationList(makeNotification).filter(
        n => n.content?.length,
      ),
    [notificationRawList],
  );
  const listGroupByDate = groupBy(list, i =>
    isToday(i.createdAt) ? 'today' : 'oldest',
  ) as {
    today: DisplayedNotification[];
    oldest: DisplayedNotification[];
  };

  return (
    <VStack className="max-h-[788px] overflow-x-hidden overflow-y-auto">
      <NotificationGroup list={listGroupByDate?.today ?? []} title="today" />
      <NotificationGroup list={listGroupByDate?.oldest ?? []} title="oldest" />
    </VStack>
  );
};

const NotificationGroup = <T extends DisplayedNotification>({
  list,
  title,
}: NotificationGroupProps<T>) => {
  return (
    <VStack className="w-full divide-y divide-gray-200">
      <div
        className={cn(
          'sticky top-0 w-full bg-white px-4 pt-[12px] pb-[7px] text-body-md font-semibold capitalize',
        )}
      >
        {title}
      </div>

      <VStack className="w-full divide-y divide-gray-300">
        {list.map((item, index) => (
          <NotificationItem key={item.id ?? index} item={item} />
        ))}
      </VStack>
    </VStack>
  );
};

export default NotificationList;
